import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useRouter, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/ui/text";
import { VStack } from "@/components/ui/v-stack";
import { WorkspaceShell } from "@/components/ui/workspace-shell";
import { Icon } from "@/components/ui/icons";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { NegotiationHistoryPanel } from "@/components/negotiations/negotiation-history-panel";
import { getLoad, getLoadStatusLabel, type Load } from "@/lib/api/loads";
import { getCurrentUser } from "@/lib/api/auth";
import { getLocalizedName } from "@/lib/localization";
import { getOriginStop, getDestinationStop } from "@/lib/load-stops";
import { fromIntlLanguage } from "@/lib/i18n/config";

function NegotiationHistoryPage() {
  const search = useSearch({ strict: false }) as { load?: string };
  const router = useRouter();
  const loadId = search.load ?? null;
  const { t: tNegotiations } = useTranslation("negotiations");
  const { t: tLoads } = useTranslation("loads");
  const { t: tCommon } = useTranslation("common");
  const language = fromIntlLanguage(useTranslation().i18n.language);

  const [loadInfo, setLoadInfo] = useState<Load | null>(null);
  const [loading, setLoading] = useState(!!loadId);

  useEffect(() => {
    if (!loadId) return;
    let cancelled = false;
    getLoad(loadId)
      .then((data) => {
        if (!cancelled) {
          setLoadInfo(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadId]);

  const statusLabel = loadInfo
    ? getLoadStatusLabel(loadInfo.status, tLoads)
    : "";

  const routeLabel = loadInfo
    ? `${getLocalizedName(getOriginStop(loadInfo.stops)?.city, language)} → ${getLocalizedName(getDestinationStop(loadInfo.stops)?.city, language)}`
    : "";

  if (!loadId) {
    return (
      <WorkspaceShell center data-testid="negotiation-history-page">
        <EmptyState
          title={tNegotiations("noMessagesYet")}
          description={tNegotiations("selectLoadToViewNegotiations")}
          icon={<Icon.Negotiations size="lg" color="muted" />}
        />
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell data-testid="negotiation-history-page" className="gap-4">
      {loading || !loadInfo ? (
        <PageNavigationHeader
          contextLabel={tNegotiations("negotiationHistory")}
          title={tCommon("loading")}
          metadata={tCommon("loading")}
          status={<Skeleton className="h-7 w-24" rounded="full" />}
          onBack={() => router.history.back()}
          backAriaLabel={tCommon("back")}
        />
      ) : (
        <PageNavigationHeader
          contextLabel={tNegotiations("negotiationHistory")}
          title={`${loadInfo.load_id} · ${routeLabel}`}
          metadata={`$${loadInfo.price.toLocaleString()}`}
          status={
            <EntityStatusBadge
              entityType="load"
              status={loadInfo.status}
              className="px-3 py-1"
            >
              {statusLabel}
            </EntityStatusBadge>
          }
          onBack={() => router.history.back()}
          backAriaLabel={tCommon("back")}
        />
      )}

      <VStack fill gap="none" className="min-h-0">
        <NegotiationHistoryPanel
          loadId={loadId}
          readOnly
          isAdminView={getCurrentUser()?.is_admin === true}
          className="h-full"
        />
      </VStack>
    </WorkspaceShell>
  );
}

export const Route = createFileRoute("/_app/negotiations/history")({
  component: NegotiationHistoryPage,
});
